<template>
    <div class="cz2__panel cz2__panel--secondary cz2__panel--secondary-fullscreen cz2__review">
        <focus-lock :disabled="focusLockDisabled">
            <div class="cz2__panel__header hide--below-sm">
                <details-header></details-header>
            </div>

            <div class="cz2__panel__body">
                <panel-header :title="$t('REVIEW_ORDER')" align="left" />

                <p class="cz2__review__copy cz2__type__small">
                    {{$t('REVIEW_COPY')}}
                </p>

                <!--
                <p class="cz2__review__copy cz2__type__tiny cz2__type__muted">
                    {{$t('REVIEW_DISCLAIMER')}}
                </p>
                -->

                <div class="cz2__review__preview">
                    <button class="cz2__review__close" :aria-label="$t('MENU_CLOSE')" @click.prevent="cancel">
                    </button>

                    <renderer :embedded="true"></renderer>
                </div>

                <details-header class="hide--above-sm cz2__review__header"></details-header>

                <div class="cz2__menu__items cz2__review__menu">
                    <menu-item class="cz2__menu-item--full-width"
                        :title="$t('MENU_PRODUCT_INFO')"
                        type="product-info"
                        @selected="selectStep('info', 'review')"
                    ></menu-item>

                    <menu-item class="cz2__menu-item--full-width"
                        :title="colorStepTitle"
                        type="color"
                        :description="selectedColor"
                        :color="selectedColorHex"
                    ></menu-item>
                </div>

                <template v-if="isGroupOrder">
                    <div class="cz2__review__totals">
                        <div class="cz2__review-totals__actions">
                            <div class="cz2__review-totals__caption">
                                {{$t('REVIEW_BOTTLES')}}
                            </div>

                            <div class="cz2__review-totals__info">
                                {{secondaryBottles.length}}
                            </div>
                        </div>

                        <div class="cz2__review-totals__price">
                            <div class="cz2__review-totals__caption">
                                {{$t('REVIEW_TOTALS')}}
                            </div>

                            <div class="cz2__review-totals__info">
                                {{subtotalPrice}}
                                <span
                                    v-if="isDiscounted"
                                    class="cz2__price--discount"
                                >
                                    {{defaultSubtotalPrice}}
                                </span>
                            </div>
                        </div>
                    </div>
                </template>

                <template v-if="!isGroupOrder">
                    <div class="cz2__review__totals">
                        <div class="cz2__review-totals__actions">
                            <div class="cz2__review-totals__caption">
                                {{$t('REVIEW_BOTTLES')}}
                            </div>

                            <div class="cz2__review-totals__info">
                                <number-selector
                                    :value="quantity"
                                    :min="1"
                                    :max="maxQuantity"
                                    @select="setQuantity($event)"
                                    @decrease="setQuantity(quantity - 1)"
                                    @increase="setQuantity(quantity + 1)"
                                />
                            </div>
                        </div>

                        <div class="cz2__review-totals__price">
                            <div class="cz2__review-totals__caption">
                                {{$t('REVIEW_TOTALS')}}
                            </div>

                            <div class="cz2__review-totals__info">
                                {{subtotalPrice}}
                                <span
                                    v-if="isDiscounted"
                                    class="cz2__price--discount"
                                >
                                    {{defaultSubtotalPrice}}
                                </span>
                            </div>
                        </div>
                    </div>
                </template>

                <!-- Add to cart / Update CTA -->
                <div class="cz2__review__actions">
                    <button class="cz2__panel-action cz2__panel-action--primary" @click.prevent="accept">
                        <span>{{actionCaption}}</span>
                    </button>
                </div>

                <div class="cz2__review__share">
                    <button :class="[
                                'cz2__image-button',
                                'cz2__image-button--save',
                                'cz2__image-button--with-text',
                                {
                                    'cz2__image-button--waiting': savedRecipe === null,
                                }
                            ]"
                        :disabled="savedRecipe === null"
                        :aria-label="$t('REVIEW_SAVE')"
                        @click.prevent="saveWishlist">
                        <span>{{$t('REVIEW_SAVE')}}</span>
                    </button>

                    <button v-for="proof in proofs" :key="proof.name"
                        class="cz2__image-button cz2__image-button--download cz2__image-button--with-text"
                        :aria-label="$t('REVIEW_PROOF') + ': ' + proof.name"
                        @click.prevent="download(proof)">
                        <span>{{$t('REVIEW_PROOF')}}<br/>{{proof.name}}</span>
                    </button>

                    <button :class="[
                                'cz2__image-button',
                                'cz2__image-button--share',
                                'cz2__image-button--with-text',
                                {
                                    'cz2__image-button--waiting': savedRecipe === null,
                                }
                            ]"
                        :aria-label="shareTitle"
                        @click.prevent="share"
                        :disabled="savedRecipe === null"
                    >
                        <span>{{shareTitle}}</span>
                    </button>

                    <button class="cz2__image-button cz2__image-button--help cz2__image-button--with-text"
                        :aria-label="$t('REVIEW_HELP')"
                        @click.prevent="help">
                        <span>{{$t('REVIEW_HELP')}}</span>
                    </button>
                </div>
            </div>

            <div class="cz2__panel__actions">
                <button class="cz2__panel-action cz2__panel-action--cancel hide--below-sm" @click.prevent="cancel"><span>{{$t('MENU_BACK')}}</span></button>

                <button class="cz2__panel-action cz2__panel-action--primary hide--above-sm" @click.prevent="accept">
                    <span>{{actionCaption}}</span>
                </button>
            </div>
        </focus-lock>
    </div>
</template>

<style lang="scss">
    @import "../style/variables.scss";

    #cz2.cz2 {
        .cz2__review {
            @include respond-below(sm) {
                border-left: none;
            }

            .cz2__panel-header {
                @include respond-below(sm) {
                    min-height: 0;
                }
            }
        }

        .cz2__review__close {
            position: absolute;

            left: 20px;
            top: 20px;

            width: 44px;
            height: 44px;

            z-index: 10;

            &:after {
                content: '';

                display: block;
                position: absolute;

                left: 0;
                top: 0;

                width: 44px;
                height: 44px;

                background: transparent url(../assets/global-close@2x.png) no-repeat center/contain;
            }
        }

        .cz2__review__actions {
            display: none;

            @include respond-above(sm) {
                display: block;
            }
        }

        .cz2__review__copy {
            margin-bottom: 20px;

            @include respond-above(sm) {
                margin-bottom: 40px;
            }
        }

        .cz2__review__menu.cz2__menu__items {
            height: auto;
            display: block;
        }

        .cz2__review__totals {
            display: flex;

            margin-bottom: 20px;

            @include respond-above(sm) {
                margin-top: 20px;
            }
        }

        .cz2__review-totals__actions {
            flex: 1 0 auto;
            width: 1px;

            text-align: left;
        }

        .cz2__review-totals__price {
            flex: 1 0 auto;
            width: 1px;

            text-align: right;
        }

        .cz2__review-totals__caption {
            margin-bottom: 10px;

            font-family: $font-main;
            font-style: normal;
            font-weight: normal;
            font-size: 12px;
            line-height: 16px;
            color: #63666a;
        }

        .cz2__review-totals__info {
            font-family: $font-main;
            font-style: normal;
            font-weight: normal;
            font-size: 24px;
            line-height: 30px;

            .cz2__number-selector {
                max-width: 150px;
            }

            .cz2__price--discount {
                color: #63666a;
                text-decoration: line-through;
                margin-left: 4px;
                display: inline-block;
            }

            .cz2__price--strike {
                display: block;

                margin-top: -5px;

                font-size: 16px;
                text-decoration: line-through;
            }
        }

        .cz2__review__share {
            display: flex;
            justify-content: space-between;

            margin-top: 30px;

            margin-bottom: 100px;

            @include respond-above(sm) {
                margin-bottom: 0px;
            }

            .cz2__image-button {
                flex: 0 0 auto;
            }

            .cz2__image-button--share {
                &:disabled {
                    cursor: wait;
                    opacity: .35;
                }
            }
        }

        .cz2__review__preview {
            position: relative;

            width: calc(100% + 40px);
            margin-left: -20px;
            height: 350px;

            background: #fff;

            border-top: 1px solid #e2e2e2;
            border-bottom: 1px solid #e2e2e2;

            @include respond-above(sm) {
                display: none;
            }

            .cz2__renderer {
                height: 348px;
            }
        }

        .cz2__review__header {
            display: block;

            margin-top: 20px;
            margin-bottom: 20px;

            .cz2__product-title {
                white-space: normal;
                width: auto;
                margin-bottom: 5px;
            }
        }
    }
</style>

<script>
    import axios from 'axios';
    import FocusLock from 'vue-focus-lock';
    import FileSaver from 'file-saver';
    import Cookies from 'js-cookie';

    import DetailsHeader from './DetailsHeader.vue';
    import PanelHeader from './PanelHeader.vue';
    import MenuItem from './MenuItem.vue';
    import NumberSelector from './NumberSelector.vue';
    import Renderer from './Renderer.vue';

    import * as constants from '../constants';

    export default {
        components: {
            FocusLock,
            DetailsHeader,
            MenuItem,
            PanelHeader,
            NumberSelector,
            Renderer,
        },
        data() {
            return {
                focusLockDisabled: true,
                savedRecipe: null,
            };
        },
        computed: {
            /**
             * Current view controller.
             */
            viewController() {
                return this.$store.state.customizer.viewController;
            },

            /**
             * Can we build this configuration?
             */
            canBuild() {
                return this.$store.getters.canBuild;
            },

            /**
             * Color step caption.
             */
            colorStepTitle() {
                return this.$store.getters.colorStepTitle;
            },

            /**
             * Currently selected color name.
             */
            selectedColor() {
                const color = this.$store.getters.selectedColor;

                const method = this.$store.state.customizer.personalizationMethod;

                let methodName = '';

                if (method === constants.METHOD_PRINTING) {
                    methodName = this.$t('PRINTING_PRINTING');
                }

                if (method === constants.METHOD_ETCHING) {
                    methodName = this.$t('PRINTING_ETCHING');
                }

                return `${color} / ${methodName}`;
            },

            /**
             * Currently selected color hex color.
             */
            selectedColorHex() {
                return this.$store.getters.selectedColorHex;
            },

            isGroupOrder() {
                return this.$store.state.isGroupOrder;
            },

            secondaryBottles() {
                return this.$store.state.customizer.secondaryBottles;
            },

            actionCaption() {
                if (this.$store.state.customizer.options.recipe && this.$store.state.customizer.options.recipe !== 'null') {
                    return this.$t('ACTION_UPDATE');
                }

                return this.$t('ACTION_ADD_TO_CART');
            },

            subtotalPrice() {
                return this.$store.state.customizer.subtotalPrice;
            },

            defaultSubtotalPrice() {
                return this.$store.state.customizer.defaultSubtotalPrice;
            },

            isDiscounted() {
                return this.subtotalPrice !== this.defaultSubtotalPrice;
            },

            quantity() {
                return this.$store.state.customizer.quantity;
            },

            maxQuantity() {
                // return Math.min(150, this.$store.getters.availableQuantity);
                return this.$store.getters.availableQuantity;
            },

            proofs() {
                const views = [];

                const dependentViews = [];

                this.viewController.blueprint.views.forEach((v) => {
                    if (v.custom && v.custom['v-also-show']) {
                        dependentViews.push(v.custom['v-also-show']);
                    }
                });

                this.viewController.blueprint.views.forEach((view) => {
                    if (dependentViews.includes(view.code)) {
                        return;
                    }

                    if (!view.productionReady && view.bakeInRecipe) {
                        let bakedFrame;

                        if (view.bakedFrame) {
                            ({ bakedFrame } = view);
                        } else {
                            if (view.viewType === 'Single') {
                                bakedFrame = view.frameLow;
                            }
                            if (view.viewType === 'Range') {
                                bakedFrame = view.frameLow;
                            }
                        }

                        let url = this.viewController.makeFrameUrl(view, this.viewController.state, bakedFrame, {
                            baking: true,
                        });

                        url += '&fmt=jpg&qlt=95';

                        url = url.replace('rhei=600&', '');
                        url = url.replace('rwid=500&', '');

                        views.push({
                            name: view.name,
                            url,
                        });
                    }
                });

                return views;
            },

            /**
             * Gets the selected bottle index.
             */
            selectedBottleIndex() {
                const bottle = this.$store.state.customizer.selectedBottle;
                const bottles = this.$store.state.customizer.secondaryBottles;

                if (bottle && bottles) {
                    return bottles.indexOf(bottle);
                }

                return -1;
            },

            shareTitle() {
                if (navigator.share) {
                    return this.$t('REVIEW_SHARE');
                }

                return this.$t('REVIEW_EMAIL');
            },
        },
        watch: {
            savedRecipe() {
                let url = new window.URL(window.location.href);

                url.searchParams.delete('edit');
                url.searchParams.append('edit', this.savedRecipe.recipe.id);

                url = url.toString();

                window.history.replaceState(null, null, url);
            },
        },
        mounted() {
            // Otherwise this messes with transition animation because browsers will try to scroll to the focused item, which would be off-screen.
            this.$store.state.eventBus.$once('secondaryEntered', () => {
                // this.focusLockDisabled = false;
            });

            // Save a recipe here to be able to share synchronously later
            this.$store.dispatch('customizerDone', {
                saveOnly: true,
                noBusy: true,
            }).then((recipe) => {
                this.savedRecipe = recipe;
            });
        },
        methods: {
            /**
             * Add to cart.
             */
            accept() {
                // Are bottles customized with different text?
                if (this.isGroupOrder) {
                    const bottles = this.$store.state.customizer.secondaryBottles;

                    const uniqueText = new Set();
                    let haveEmpty = false;

                    if (bottles) {
                        bottles.forEach((bottle) => {
                            const text = bottle.map((x) => (x || '').trim()).join('|');

                            if (text.replace(/\|/g, '').length === 0) {
                                haveEmpty = true;
                            }

                            uniqueText.add(text);
                        });
                    }

                    const uniqueCount = uniqueText.size;

                    if (uniqueCount < 2 || haveEmpty) {
                        this.$store.dispatch('showGenericConfirm', {
                            title: this.$t('NOGROUP_TITLE'),
                            description: this.$t('NOGROUP_BODY'),
                            confirmAction: this.$t('NOGROUP_ACCEPT'),
                            cancelAction: this.$t('NOGROUP_CANCEL'),
                            confirmationCallback: () => {
                                this.$store.dispatch('setIsGroupOrder', false);
                            },
                        });

                        // Exit regardless.
                        return;
                    }
                }

                let actualFilledTextPlacements = this.$store.getters.filledTextPlacements.length;

                if (!this.isGroupOrder) {
                    actualFilledTextPlacements = 0;

                    this.$store.getters.textPlacements.forEach((c) => {
                        if (c.component.custom
                            && c.component.custom['personalization-text']
                            && c.component.custom['personalization-text'].trim().length > 0) {
                            actualFilledTextPlacements += 1;
                        }
                    });
                }

                if (actualFilledTextPlacements === 0
                    && this.$store.getters.filledMonogramPlacements.length === 0
                    && this.$store.getters.filledImagePlacements.length === 0) {
                    this.$store.dispatch('showGenericNotify', {
                        title: this.$t('NO_PERSONALIZATIONS'),
                        description: this.$t('NO_PERSONALIZATIONS_BODY'),
                        action: this.$t('ACTION_CONTINUE'),
                    });
                } else if (this.canBuild) {
                    if (Cookies.get('CUSTOM-TOS') === 'accepted') {
                        this.$store.dispatch('customizerDone');
                    } else {
                        this.$store.dispatch('showTosConfirmation', true);
                    }
                }
            },

            /**
             * Select a step.
             */
            selectStep(step, returnTo) {
                if (returnTo) {
                    this.$store.dispatch('selectStep', {
                        step,
                        parameters: {
                            returnTo,
                        },
                    });
                } else {
                    this.$store.dispatch('selectStep', step);
                }
            },

            /**
             * Go back.
             */
            cancel() {
                // Go back to the main screen.
                this.$store.dispatch('selectStep', null);
            },

            setQuantity(v) {
                this.$store.dispatch('setQuantity', v);
            },

            saveWishlist() {
                // Need a recipe to share it
                if (!this.savedRecipe) {
                    return;
                }

                this.$store.dispatch('saveWishlist', {
                    id: this.savedRecipe.recipe.id,
                });
            },

            download(view) {
                const isIos = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

                if (isIos) {
                    window.open(view.url);
                } else {
                    axios.get(view.url, { responseType: 'blob' }).then((response) => {
                        let name;

                        if (this.isGroupOrder) {
                            name = `Proof-${this.selectedBottleIndex}-${view.name}.jpg`;
                        } else {
                            name = `Proof-${view.name}.jpg`;
                        }

                        FileSaver.saveAs(response.data, name);
                    });
                }
            },

            share() {
                // Need a recipe to share it
                if (!this.savedRecipe) {
                    return;
                }

                let url = new window.URL(window.location.href);

                url.searchParams.delete('edit');
                url.searchParams.append('edit', this.savedRecipe.recipe.id);

                url = url.toString();

                if (navigator.share) {
                    navigator.share({
                        url,
                    });
                } else {
                    window.location.href = `mailto:?body=${encodeURIComponent(url)}`;
                }
            },

            help() {
                this.$store.dispatch('selectStep', {
                    step: 'help',
                    parameters: {
                        returnTo: 'review',
                    },
                });
            },
        },
    };
</script>
